import { useState } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from '@/utils/authenticatedFetch';

interface LedgerEntry {
  id: string;
  entryNumber: string;
  description: string;
  reference?: string;
  transactionType: string;
  amount: number;
  debitOrCredit: 'debit' | 'credit';
  accountId: string;
  accountName: string;
  accountType: string;
  businessId: string;
  branchId: string;
  createdAt: string;
  createdBy: string;
}

interface LedgerAccount {
  id: string;
  accountCode: string;
  accountName: string;
  accountType: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  subType: string;
  currency: string;
  description?: string;
  isActive: boolean;
}

interface TrialBalance {
  period: {
    fromDate: string;
    toDate: string;
  };
  accounts: Array<{
    accountId: string;
    accountCode: string;
    accountName: string;
    accountType: string;
    debitBalance: number;
    creditBalance: number;
    balance: number;
  }>;
  totalDebits: number;
  totalCredits: number;
  isBalanced: boolean;
}

export default function SmartLedgerPage() {
  const [activeTab, setActiveTab] = useState<'entries' | 'accounts' | 'trial-balance' | 'statements'>('entries');
  const [showCreateEntry, setShowCreateEntry] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState<'income-statement' | 'balance-sheet' | 'cash-flow'>('income-statement');
  const [dateRange, setDateRange] = useState({
    fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
  });
  const queryClient = useQueryClient();

  const { data: entries = [] as LedgerEntry[], isLoading: entriesLoading } = useQuery<LedgerEntry[]>({
    queryKey: ['ledger-entries', dateRange],
    queryFn: async () => {
      const params = new URLSearchParams({
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      });
      
      const response = await authenticatedFetch(`/api/smart-ledger/entries?${params}`);
      if (!response.ok) throw new Error('Failed to fetch ledger entries');
      const result = await response.json();
      return result.data;
    }
  });

  const { data: accounts = [] as LedgerAccount[], isLoading: accountsLoading } = useQuery({
    queryKey: ['ledger-accounts'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/smart-ledger/accounts');
      if (!response.ok) throw new Error('Failed to fetch ledger accounts');
      const result = await response.json();
      return result.data;
    }
  });

  const { data: trialBalance = {} as TrialBalance, isLoading: trialBalanceLoading } = useQuery({
    queryKey: ['trial-balance', dateRange],
    queryFn: async () => {
      const params = new URLSearchParams({
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      });
      
      const response = await authenticatedFetch(`/api/smart-ledger/trial-balance?${params}`);
      if (!response.ok) throw new Error('Failed to fetch trial balance');
      const result = await response.json();
      return result.data;
    }
  });

  const { data: financialStatement = {}, isLoading: statementLoading } = useQuery({
    queryKey: ['financial-statement', selectedStatement, dateRange],
    queryFn: async () => {
      const params = new URLSearchParams({
        type: selectedStatement,
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      });
      
      const response = await authenticatedFetch(`/api/smart-ledger/financial-statements?${params}`);
      if (!response.ok) throw new Error('Failed to fetch financial statement');
      const result = await response.json();
      return result.data;
    },
    enabled: !!selectedStatement
  });

  const createEntryMutation = useMutation({
    mutationFn: async (entryData: any) => {
      const response = await authenticatedFetch('/api/smart-ledger/journal-entries', {
        method: 'POST',
        body: JSON.stringify(entryData),
      });
      if (!response.ok) throw new Error('Failed to create journal entry');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ledger-entries'] });
      queryClient.invalidateQueries({ queryKey: ['trial-balance'] });
      setShowCreateEntry(false);
    }
  });

  const createAccountMutation = useMutation({
    mutationFn: async (accountData: any) => {
      const response = await authenticatedFetch('/api/smart-ledger/accounts', {
        method: 'POST',
        body: JSON.stringify(accountData),
      });
      if (!response.ok) throw new Error('Failed to create ledger account');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ledger-accounts'] });
      setShowCreateAccount(false);
    }
  });

  const handleCreateEntry = async (formData: any) => {
    try {
      await createEntryMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Create entry error:', error);
    }
  };

  const handleCreateAccount = async (formData: any) => {
    try {
      await createAccountMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Create account error:', error);
    }
  };

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case 'asset': return 'bg-blue-100 text-blue-800';
      case 'liability': return 'bg-red-100 text-red-800';
      case 'equity': return 'bg-green-100 text-green-800';
      case 'revenue': return 'bg-purple-100 text-purple-800';
      case 'expense': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return `MWK ${amount.toLocaleString('en-MW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (entriesLoading || accountsLoading || trialBalanceLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Smart Ledger Engine</h1>
          <p className="text-gray-600">Advanced double-entry bookkeeping with automated reconciliation</p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => setShowCreateEntry(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Create Journal Entry
          </Button>
          <Button
            onClick={() => setShowCreateAccount(true)}
            variant="ghost"
          >
            Create Account
          </Button>
        </div>
      </div>
      {/* Header */}
      {/* Header */}

      {/* Date Range Filter */}
      <Card>
        <Card.Header>
          <Card.Title>Date Range</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="flex flex-col md:flex-row gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">From Date</label>
              <Input
                type="date"
                value={dateRange.fromDate}
                onChange={(e: any) => setDateRange(prev => ({ ...prev, fromDate: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">To Date</label>
              <Input
                type="date"
                value={dateRange.toDate}
                onChange={(e: any) => setDateRange(prev => ({ ...prev, toDate: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
        </Card.Content>
      </Card>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'entries', label: 'Ledger Entries', count: entries?.length || 0 },
            { id: 'accounts', label: 'Chart of Accounts', count: accounts?.length || 0 },
            { id: 'trial-balance', label: 'Trial Balance', count: null },
            { id: 'statements', label: 'Financial Statements', count: null },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Create Journal Entry Form */}
      {showCreateEntry && (
        <Card>
          <Card.Header>
            <Card.Title>Create Journal Entry</Card.Title>
          </Card.Header>
          <Card.Content>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const entries = Array.from(formData.getAll('entries')).map((entry: any) => JSON.parse(entry));
                handleCreateEntry({
                  description: formData.get('description'),
                  reference: formData.get('reference'),
                  entries,
                });
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <Input name="description" required className="mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reference</label>
                  <Input name="reference" className="mt-1" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Entries</label>
                <div className="mt-1 space-y-2">
                  {accounts?.slice(0, 4).map((_: any, index: number) => (
                    <div key={index} className="flex space-x-2">
                      <select name={`entries-${index}-accountId`} className="flex-1 px-3 py-2 border border-gray-300 rounded-md">
                        <option value="">Select account</option>
                        {accounts.map((acc: any) => (
                          <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                        ))}
                      </select>
                      <Input name={`entries-${index}-amount`} type="number" step="0.01" placeholder="Amount" className="w-32" />
                      <select name={`entries-${index}-debitOrCredit`} className="px-3 py-2 border border-gray-300 rounded-md">
                        <option value="debit">Debit</option>
                        <option value="credit">Credit</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateEntry(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Create Entry</Button>
              </div>
            </form>
          </Card.Content>
        </Card>
      )}

      {/* Create Account Form */}
      {showCreateAccount && (
        <Card>
          <Card.Header>
            <Card.Title>Create Ledger Account</Card.Title>
          </Card.Header>
          <Card.Content>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                handleCreateAccount({
                  accountCode: formData.get('accountCode'),
                  accountName: formData.get('accountName'),
                  accountType: formData.get('accountType'),
                  subType: formData.get('subType'),
                  currency: formData.get('currency'),
                  description: formData.get('description'),
                });
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Code</label>
                  <Input name="accountCode" required className="mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Name</label>
                  <Input name="accountName" required className="mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Type</label>
                  <select name="accountType" required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="">Select type</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                    <option value="equity">Equity</option>
                    <option value="revenue">Revenue</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sub Type</label>
                  <Input name="subType" required className="mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <select name="currency" required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                    <option value="MWK">MWK</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea name="description" rows={3} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateAccount(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Create Account</Button>
              </div>
            </form>
          </Card.Content>
        </Card>
      )}

      {/* Tab Content */}
      {activeTab === 'entries' && (
        <Card>
          <Card.Header>
            <Card.Title>Ledger Entries</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entry #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Debit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {entries?.map((entry: any) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.entryNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{entry.description}</div>
                        {entry.reference && (
                          <div className="text-sm text-gray-500">Ref: {entry.reference}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.accountName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getAccountTypeColor(entry.accountType)}`}>
                          {entry.accountType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.debitOrCredit === 'debit' ? formatCurrency(entry.amount) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.debitOrCredit === 'credit' ? formatCurrency(entry.amount) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {(!entries || entries.length === 0) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No ledger entries found</p>
                </div>
              )}
            </div>
          </Card.Content>
        </Card>
      )}

      {activeTab === 'accounts' && (
        <Card>
          <Card.Header>
            <Card.Title>Chart of Accounts</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Balance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accounts?.map((account: any) => (
                    <tr key={account.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {account.accountCode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {account.accountName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getAccountTypeColor(account.accountType)}`}>
                          {account.accountType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(account.balance)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          account.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {account.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {(!accounts || accounts.length === 0) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No accounts found</p>
                </div>
              )}
            </div>
          </Card.Content>
        </Card>
      )}

      {activeTab === 'trial-balance' && trialBalance && (
        <Card>
          <Card.Header>
            <Card.Title>Trial Balance</Card.Title>
            <p className="text-sm text-gray-600">
              {new Date(trialBalance.period.fromDate).toLocaleDateString()} - {new Date(trialBalance.period.toDate).toLocaleDateString()}
            </p>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600">Total Debits</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(trialBalance.totalDebits)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">Total Credits</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(trialBalance.totalCredits)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600">Balance Status</p>
                  <p className={`text-2xl font-bold ${trialBalance.isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                    {trialBalance.isBalanced ? 'Balanced' : 'Out of Balance'}
                  </p>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Account Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Account Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Debit Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Credit Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trialBalance.accounts.map((account: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {account.accountCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {account.accountName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {account.debitBalance > 0 ? formatCurrency(account.debitBalance) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {account.creditBalance > 0 ? formatCurrency(account.creditBalance) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card.Content>
        </Card>
      )}

      {activeTab === 'statements' && (
        <Card>
          <Card.Header>
            <Card.Title>Financial Statements</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              <div className="flex space-x-2">
                <Button
                  variant={selectedStatement === 'income-statement' ? 'primary' : 'ghost'}
                  onClick={() => setSelectedStatement('income-statement')}
                >
                  Income Statement
                </Button>
                <Button
                  variant={selectedStatement === 'balance-sheet' ? 'primary' : 'ghost'}
                  onClick={() => setSelectedStatement('balance-sheet')}
                >
                  Balance Sheet
                </Button>
                <Button
                  variant={selectedStatement === 'cash-flow' ? 'primary' : 'ghost'}
                  onClick={() => setSelectedStatement('cash-flow')}
                >
                  Cash Flow Statement
                </Button>
              </div>
              
              {statementLoading ? (
                <div className="flex items-center justify-center h-32">
                  <LoadingSpinner size="md" />
                </div>
              ) : financialStatement ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    Period: {new Date(financialStatement.period.fromDate).toLocaleDateString()} - {new Date(financialStatement.period.toDate).toLocaleDateString()}
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <pre className="text-sm whitespace-pre-wrap">
                      {JSON.stringify(financialStatement.data, null, 2)}
                    </pre>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button onClick={() => window.open(`/api/smart-ledger/export?format=csv&fromDate=${dateRange.fromDate}&toDate=${dateRange.toDate}`, '_blank')}>
                      Export CSV
                    </Button>
                    <Button onClick={() => window.open(`/api/smart-ledger/export?format=pdf&fromDate=${dateRange.fromDate}&toDate=${dateRange.toDate}`, '_blank')}>
                      Export PDF
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No financial statement data available</p>
                </div>
              )}
            </div>
          </Card.Content>
        </Card>
      )}
    </div>
  );
}
